import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import api from './utils/api';

// Lazy-loaded page components
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const CasesPage = React.lazy(() => import('./pages/CasesPage'));
const CaseDetailPage = React.lazy(() => import('./pages/CaseDetailPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const AIFeedbackPage = React.lazy(() => import('./pages/AIFeedbackPage'));
const DamageIntakePage = React.lazy(() => import('./pages/DamageIntakePage'));
const BulkUploadPage = React.lazy(() => import('./pages/BulkUploadPage'));
const VehicleDebugPage = React.lazy(() => import('./pages/VehicleDebugPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const ConfigurationPage = React.lazy(() => import('./pages/ConfigurationPage'));
const ApiDocumentationPage = React.lazy(() => import('./pages/ApiDocumentationPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const PotentialDuplicatesPage = React.lazy(() => import('./pages/PotentialDuplicatesPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const ChargeDetailPage = React.lazy(() => import('./pages/ChargeDetailPage'));
const MemberDetailPage = React.lazy(() => import('./pages/MemberDetailPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle Google OAuth session_id callback
  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.slice(1));
    const sessionId = hashParams.get('session_id');

    if (sessionId) {
      // Exchange session_id for JWT token
      const exchangeSession = async () => {
        try {
          const response = await api.post('/auth/session', { session_id: sessionId });
          
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            setIsAuthenticated(true);
            toast.success('Login successful!');
            // Remove session_id from URL and navigate to dashboard
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          // Handle specific error cases
          const errorMessage = error.response?.data?.detail || 'Authentication failed';
          toast.error(errorMessage);
          
          // Navigate to login page and show the error
          navigate('/login', { replace: true });
        }
      };

      exchangeSession();
    } else {
      // Check for existing JWT token
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
      }
    }
  }, [location, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <DashboardLayout onLogout={handleLogout}>
                <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="text-slate-600 dark:text-slate-400">Loading...</div></div>}>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/cases" element={<CasesPage />} />
                    <Route path="/case/:caseId" element={<CaseDetailPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/ai-feedback" element={<AdminRoute><AIFeedbackPage /></AdminRoute>} />
                    <Route path="/potential-duplicates" element={<ProtectedRoute><PotentialDuplicatesPage /></ProtectedRoute>} />
                    <Route path="/intake" element={<DamageIntakePage />} />
                    <Route path="/bulk-upload" element={<BulkUploadPage />} />
                    <Route path="/vehicle-debug" element={<VehicleDebugPage />} />
                    <Route path="/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
                    <Route path="/config" element={<AdminRoute><ConfigurationPage /></AdminRoute>} />
                    <Route path="/api-docs" element={<AdminRoute><ApiDocumentationPage /></AdminRoute>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/billing" element={<BillingPage />} />
                    <Route path="/billing/charges/:chargeId" element={<ChargeDetailPage />} />
                    <Route path="/billing/members/:memberId" element={<MemberDetailPage />} />
                    <Route path="/billing/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </React.Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  // Initialize theme on app load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <Router>
        <AppContent />
      </Router>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

// Admin-only route protection
function AdminRoute({ children }) {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  if (userRole !== 'admin') {
    toast.error('Access denied. Admin privileges required.');
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

export default App;
export { api };